import cookies from 'js-cookie';
import moment from 'moment-timezone';
import {
  generateCashupReportEscPos,
  type CashupReportDateFormat,
  type CashupReportTicketParams,
  type EscPosPaperWidth,
} from '@washday/utilities';
import type { ICashUpReport, IStore } from '../interfaces/Store';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import { openEscPosFallbackTab, reserveEscPosFallbackTab } from './escposFallbackTab';
import { showNotification, updateNotification } from './Notification/Notification';
import Printer from './qz-print';
import { formatMoneyString } from './strings';
import { createEscPosQzPrintData, resolveEscPosPaperWidth } from './ticketEscPos';

type CashupReportTranslator = (key: string) => string;

export type GenerateCashupReportEscPosParams = {
  reportData: ICashUpReport;
  store?: IStore | null;
  storeCanUseMP: boolean;
  includeDetailed: boolean;
  paperWidth: EscPosPaperWidth;
  t: CashupReportTranslator;
};

export type TryPrintCashupReportEscPosParams = Omit<
  GenerateCashupReportEscPosParams,
  'paperWidth'
> & {
  failureTitle?: string;
  fallbackWindow?: Window | null;
  openBrowserFallback?: boolean;
};

const FALLBACK_PRINT_MESSAGE =
  'No pudimos imprimir directamente en la impresora. Abriremos el formato ticket para imprimir desde el navegador.';

const formatCashupDate = (
  value: Parameters<CashupReportTicketParams['formatDate']>[0],
  format: CashupReportDateFormat,
  timezone?: string
): string => {
  if (!value) return '';

  const momentFormat =
    format === 'date' ? 'DD/MM/YYYY' : format === 'time' ? 'HH:mm' : 'DD/MM/YYYY HH:mm';
  const date = moment(value);
  if (!date.isValid()) return '';

  if (timezone && moment.tz.zone(timezone)) {
    date.tz(timezone);
  }

  return date.format(momentFormat);
};

export function generateCashupReportEscPosForFrontend(
  params: GenerateCashupReportEscPosParams
): Uint8Array {
  return generateCashupReportEscPos({
    ...params,
    formatMoney: formatMoneyString,
    formatDate: (value, format) => formatCashupDate(value, format, params.store?.timezone),
    includeCut: true,
  });
}

function updateFallbackNotification(id: string, message?: string, failureTitle?: string): void {
  updateNotification({
    id,
    loading: false,
    title: failureTitle || FALLBACK_PRINT_MESSAGE,
    message,
  });
}

function openBrowserFallback({
  bytes,
  fallbackWindow,
  failureTitle,
  notificationId,
  reason,
  t,
}: {
  bytes: Uint8Array;
  fallbackWindow?: Window | null;
  failureTitle?: string;
  notificationId: string;
  reason: string;
  t: CashupReportTranslator;
}): void {
  const opened = openEscPosFallbackTab({
    bytes,
    fallbackWindow,
    title: t('CashupsPage:cashUpReport.title'),
  });

  updateFallbackNotification(
    notificationId,
    `${reason} ${
      opened
        ? t('CashupsPage:cashUpReport.print.fallbackOpened')
        : t('CashupsPage:cashUpReport.print.fallbackBlocked')
    }`,
    failureTitle
  );
}

function getConfiguredPrinterName(): string | undefined {
  return cookies.get('printer') || localStorage.getItem('printer') || undefined;
}

function offerBrowserFallback({
  bytes,
  failureTitle,
  notificationId,
  reason,
  t,
}: {
  bytes: Uint8Array;
  failureTitle?: string;
  notificationId: string;
  reason: string;
  t: CashupReportTranslator;
}): void {
  updateFallbackNotification(notificationId, reason, failureTitle);
  ConfirmationModal({
    title: failureTitle || t('CashupsPage:cashUpReport.print.fallbackPromptTitle'),
    children: `${reason} ${t('CashupsPage:cashUpReport.print.fallbackPrompt')}`,
    labels: {
      confirm: t('CashupsPage:cashUpReport.print.openFallback'),
      cancel: t('CommonButtons:cancel'),
    },
    onConfirm: () => {
      const opened = openEscPosFallbackTab({
        bytes,
        title: t('CashupsPage:cashUpReport.title'),
      });

      showNotification({
        type: opened ? 'Default' : 'Error',
        title: t(
          opened
            ? 'CashupsPage:cashUpReport.print.fallbackOpened'
            : 'CashupsPage:cashUpReport.print.fallbackBlocked'
        ),
      });
    },
  });
}

function handleBrowserFallback({
  bytes,
  fallbackWindow,
  failureTitle,
  notificationId,
  reason,
  t,
}: {
  bytes: Uint8Array;
  fallbackWindow?: Window | null;
  failureTitle?: string;
  notificationId: string;
  reason: string;
  t: CashupReportTranslator;
}): void {
  if (fallbackWindow) {
    openBrowserFallback({
      bytes,
      fallbackWindow,
      failureTitle,
      notificationId,
      reason,
      t,
    });
    return;
  }

  offerBrowserFallback({ bytes, failureTitle, notificationId, reason, t });
}

export function reserveCashupReportFallbackTab(): Window | null | undefined {
  if (import.meta.env.VITE_E2E_DISABLE_QZ === '1') {
    return null;
  }

  if (getConfiguredPrinterName()) {
    return undefined;
  }

  return reserveEscPosFallbackTab();
}

export function closeCashupReportFallbackTab(fallbackWindow?: Window | null): void {
  try {
    fallbackWindow?.close();
  } catch {
    // Best-effort cleanup must not change the saved cash-up or print result.
  }
}

function getErrorMessage(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

export async function tryPrintCashupReportEscPos(
  params: TryPrintCashupReportEscPosParams
): Promise<boolean> {
  if (import.meta.env.VITE_E2E_DISABLE_QZ === '1') {
    closeCashupReportFallbackTab(params.fallbackWindow);
    return false;
  }

  const notificationId = showNotification({
    loading: true,
    title: 'Preparando impresion de ticket',
    type: 'Info',
  });
  let bytes: Uint8Array | null = null;

  try {
    const generateBytes = () => {
      const printerPaperSize =
        cookies.get('printerPaperSize') || localStorage.getItem('printerPaperSize');
      const paperWidth = resolveEscPosPaperWidth(printerPaperSize);

      return generateCashupReportEscPosForFrontend({
        ...params,
        paperWidth,
      });
    };
    if (params.openBrowserFallback) {
      bytes = generateBytes();
    }

    const printerName = getConfiguredPrinterName();

    if (!printerName) {
      const reason = params.t('CashupsPage:cashUpReport.print.noPrinter');
      if (bytes) {
        handleBrowserFallback({
          bytes,
          fallbackWindow: params.fallbackWindow,
          failureTitle: params.failureTitle,
          notificationId,
          reason,
          t: params.t,
        });
      } else {
        closeCashupReportFallbackTab(params.fallbackWindow);
        updateFallbackNotification(notificationId, reason, params.failureTitle);
      }
      return false;
    }

    const printer = new Printer();
    const connectResponse = await printer.verifyQZConnection(printerName);

    if (!connectResponse.ok) {
      console.warn('Cashup report ESC/POS QZ connection unavailable', connectResponse);
      if (bytes) {
        handleBrowserFallback({
          bytes,
          fallbackWindow: params.fallbackWindow,
          failureTitle: params.failureTitle,
          notificationId,
          reason: connectResponse.message || FALLBACK_PRINT_MESSAGE,
          t: params.t,
        });
      } else {
        closeCashupReportFallbackTab(params.fallbackWindow);
        updateFallbackNotification(notificationId, connectResponse.message, params.failureTitle);
      }
      return false;
    }

    bytes ||= generateBytes();

    await printer.print(
      printerName,
      {
        jobName: 'Washday - Cashup Report ESC/POS',
      },
      createEscPosQzPrintData(bytes)
    );
    closeCashupReportFallbackTab(params.fallbackWindow);

    updateNotification({
      id: notificationId,
      loading: false,
      title: 'Reporte enviado a impresora',
      message: printerName,
    });
    return true;
  } catch (error) {
    console.warn('Cashup report ESC/POS print failed, falling back to HTML ticket', error);
    if (params.openBrowserFallback && bytes) {
      handleBrowserFallback({
        bytes,
        fallbackWindow: params.fallbackWindow,
        failureTitle: params.failureTitle,
        notificationId,
        reason: getErrorMessage(error) || FALLBACK_PRINT_MESSAGE,
        t: params.t,
      });
      return false;
    }

    closeCashupReportFallbackTab(params.fallbackWindow);
    updateFallbackNotification(notificationId, getErrorMessage(error), params.failureTitle);
    return false;
  }
}
